import { ApolloQueryResult } from 'apollo-client';

import { mapBannerFilterToApiFormat, mapRowsToBanners } from '../../mappers/banner/banner';
import {
    getValueFromApiFormat,
    mapAdditionalFilterToApiFormat,
    mapDateRangePresetToApiFormat,
    mapSortParamsToString,
    mapTotalStatsToApiFormat
} from '../../mappers/common/common';
import { getDefaultSort } from '../../service/GridHelpers/GridHelpers';
import { ClientInfo } from '../../typings/common';
import { BannersQuery, BannersSuggestQuery } from '../../typings/graphql-grid';
import { defaultClientInfo, GridParams, GridSourceParams } from '../../typings/grid';
import { ClientFeatures } from '../../typings/state';

import { mapGridParamsToStatRequirements } from './common';

export const mapSourceParamsToBannersPreviewQueryVariables = (
    params: GridParams,
    clientInfo: ClientInfo,
    features: ClientFeatures
) => {
    const { campaignsIds, groupsIds, statPeriod, filter, additionalFilter, view } = params;

    const apiFormatFilter = filter ? mapBannerFilterToApiFormat(filter, clientInfo, features) : {};

    if (additionalFilter !== undefined) {
        apiFormatFilter.archived = mapAdditionalFilterToApiFormat(additionalFilter);
    }

    let statRequirements;
    if (statPeriod) {
        const { statPreset, statFrom, statTo } = statPeriod;
        statRequirements = {
            preset: statPreset && mapDateRangePresetToApiFormat(statPreset),
            from: statFrom,
            to: statTo
        };
    }

    return {
        orderBy: getDefaultSort(view),
        statRequirements,
        filter: {
            ...((filter || additionalFilter) && apiFormatFilter),
            campaignIdIn: campaignsIds,
            adGroupIdIn: groupsIds && groupsIds.length !== 0 ? groupsIds : undefined
        }
    };
};

export const mapSourceParamsToBannersAndSuggestQueryVariables = (
    params: GridParams,
    clientInfo: ClientInfo,
    features: ClientFeatures
) => {
    const { campaignsIds, groupsIds, sortParams, filter, additionalFilter, view, campaignsCalcTypeFilter } = params;

    const apiFormatFilter = filter
        ? mapBannerFilterToApiFormat(filter, clientInfo, features, campaignsCalcTypeFilter)
        : {};

    if (additionalFilter !== undefined) {
        apiFormatFilter.archived = mapAdditionalFilterToApiFormat(additionalFilter);
    }

    return {
        orderBy: sortParams ? mapSortParamsToString(sortParams) : getDefaultSort(view),
        statRequirements: mapGridParamsToStatRequirements(params),
        filter: {
            ...((filter || additionalFilter) && apiFormatFilter),
            campaignIdIn: campaignsIds,
            adGroupIdIn: groupsIds && groupsIds.length !== 0 ? groupsIds : undefined
        }
    };
};

export const mapBannerQueryResultToEntitiesQueryResponse = (
    result: ApolloQueryResult<BannersQuery>,
    params: GridSourceParams
) => {
    const { client, reqId, operator, constants, placements } = result.data;
    const {
        features,
        info: { workCurrency, countryRegionId },
        ads: bannersResult
    } = client;
    const { features: operatorFeatures } = operator;
    const { rowset: bannersRows, totalCount, adIds, cacheKey, totalStats } = bannersResult;
    const { cost, clicks, shows, ctr } = totalStats;

    return {
        data: {
            data: mapRowsToBanners(
                bannersRows,
                client,
                params,
                workCurrency,
                constants.configuration.canvasUiDomain,
                placements
                    ? (placements.placements.reduce((hash, { id, operatorName }) => {
                          hash[id] = operatorName;
                          return hash;
                      }, {}) as Record<string, string>)
                    : ({} as Record<string, string>)
            ),
            total: totalCount,
            cacheKey: getValueFromApiFormat(cacheKey),
            allItemsIds: adIds.map(String)
        },
        clientInfo: {
            ...defaultClientInfo,
            ...features,
            ...operatorFeatures,
            workCurrency,
            countryRegionId
        },
        totalStats: mapTotalStatsToApiFormat({
            cost,
            clicks,
            shows,
            ctr
        }),
        hasObjectsOverLimit: bannersResult.hasObjectsOverLimit,
        reqId
    };
};

export const mapBannerSuggestQueryResultToSuggestQueryResponse = (result: ApolloQueryResult<BannersSuggestQuery>) => {
    return {
        data: result.data.client.ads.rowset.reduce((acc: string[], item: { title: string | null }) => {
            if (item.title) {
                acc.push(item.title);
            }

            return acc;
        }, []),
        reqId: result.data.reqId
    };
};
